import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
    Breadcrumb, Row, Col, Card, notification,
    Button, Spin, Image, Table, Space, Popconfirm, Tag, Modal,
} from "antd";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { PlusOutlined, EditOutlined, DeleteOutlined, MenuOutlined, SwapOutlined } from "@ant-design/icons";
import { DragHandle } from "../../components/DragHandle";
import http from "../../helpers/http";

function ProductList() {
    const navigate = useNavigate();
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [reorderModalOpen, setReorderModalOpen] = useState(false);
    const [reorderList, setReorderList] = useState([]);
    const [reorderLoading, setReorderLoading] = useState(false);

    const [searchParams, setSearchParams] = useSearchParams();
    // Default to carpets when /products is opened without a category
    const selectedCategory = searchParams.get("category")?.toLowerCase() || "carpets";

    const categories = ["carpets", "rugs", "bespoke"];
    // Remove local filtering as we will fetch per category
    // const filteredProducts = useMemo(() => {
    //     return products.filter((p) => (p.category || "").toLowerCase() === selectedCategory);
    // }, [products, selectedCategory]);

    const handleSelectCategory = (category) => {
        if (!category) {
            setSearchParams({});
        } else {
            setSearchParams({ category });
        }
    };

    const base_url = process.env.REACT_APP_API_URL + "admin/products";
    const image_url = process.env.REACT_APP_API_URL + "uploads/images/";

    const fetchProducts = async () => {
        setLoading(true);
        try {
            // Fetch with category and a high limit to ensure we see all products
            // The backend now defaults to { order: 1, createdAt: -1 }
            const res = await http.get(`${base_url}?category=${selectedCategory}&limit=1000`);
            const data = res?.data || res;
            if (data?.products) {
                setProducts(data.products);
            } else if (Array.isArray(data)) {
                setProducts(data);
            } else {
                setProducts([]);
            }
        } catch (error) {
            notification.error({ message: "Error fetching products", description: error.message });
            setProducts([]);
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            await http.delete(`${base_url}/${id}`);
            notification.success({ message: "Product deleted successfully" });
            fetchProducts();
        } catch (error) {
            notification.error({ message: "Error deleting product", description: error.message });
        }
    };

    const handleOpenReorder = () => {
        setReorderList([...products]);
        setReorderModalOpen(true);
    };

    const handleReorderSubmit = async () => {
        setReorderLoading(true);
        try {
            // Reassign order numbers based on the new array order
            const orders = reorderList.map((item, index) => ({
                id: item._id || item.id,
                order: index + 1
            }));

            await http.post(`${base_url}/reorder`, { orders });
            notification.success({ message: "Order updated successfully" });
            setReorderModalOpen(false);
            fetchProducts();
        } catch (error) {
            notification.error({ message: "Error updating order", description: error.message });
        } finally {
            setReorderLoading(false);
        }
    };

    const columns = [
        {
            title: "Image",
            dataIndex: "heroImage",
            key: "heroImage",
            width: 80,
            render: (heroImage) =>
                heroImage ? (
                    <Image
                        width={50} height={50}
                        src={image_url + heroImage}
                        style={{ objectFit: "cover", borderRadius: 4 }}
                    />
                ) : (
                    <div style={{
                        width: 50, height: 50, background: "#f0f0f0",
                        display: "flex", alignItems: "center",
                        justifyContent: "center", fontSize: 10, borderRadius: 4,
                    }}>
                        No Image
                    </div>
                ),
        },
        {
            title: "Name",
            dataIndex: "name",
            key: "name",
            sorter: (a, b) => a.name.localeCompare(b.name),
        },
        {
            title: "Availability",
            dataIndex: "availability",
            key: "availability",
            render: (availability) => (
                <Space size={[4, 4]} wrap>
                    {availability?.length > 0
                        ? availability.map((item, idx) => (
                            <Tag key={idx} color="blue">{item}</Tag>
                        ))
                        : <Tag>Not specified</Tag>
                    }
                </Space>
            ),
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (status) => (
                <Tag color={status === "active" ? "green" : status === "inactive" ? "red" : "orange"}>
                    {status?.toUpperCase()}
                </Tag>
            ),
        },
        {
            title: "Featured",
            dataIndex: "featured",
            key: "featured",
            render: (featured) => (
                <Tag color={featured ? "gold" : "default"}>
                    {featured ? "Yes" : "No"}
                </Tag>
            ),
        },
        {
            title: "Actions",
            key: "actions",
            fixed: "right",
            width: 160,
            render: (_, record) => (
                <Space>
                    <Button
                        type="primary"
                        icon={<EditOutlined />}
                        size="small"
                        onClick={() => navigate(`/products/edit/${record._id}`)}
                    >
                        Edit
                    </Button>
                    <Popconfirm
                        title="Delete this product?"
                        onConfirm={() => handleDelete(record._id)}
                        okText="Yes" cancelText="No"
                    >
                        <Button type="primary" danger icon={<DeleteOutlined />} size="small">
                            Delete
                        </Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    useEffect(() => {
        fetchProducts();
    }, [selectedCategory]);

    return (
        <div>
            <Breadcrumb style={{ margin: "16px 0" }}>
                <Breadcrumb.Item>Products</Breadcrumb.Item>
                {selectedCategory && (
                    <Breadcrumb.Item>
                        {selectedCategory.charAt(0).toUpperCase() + selectedCategory.slice(1)}
                    </Breadcrumb.Item>
                )}
            </Breadcrumb>

            <Row>
                <Col span={24}>
                    <Card
                        extra={
                            <Space>
                                <Button
                                    icon={<SwapOutlined rotate={90} />}
                                    onClick={handleOpenReorder}
                                    disabled={products.length < 2}
                                >
                                    Change Order
                                </Button>
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={() =>
                                        navigate(
                                            "/products/create" +
                                            (selectedCategory ? `?category=${selectedCategory}` : "")
                                        )
                                    }
                                >
                                    Add New Product
                                </Button>
                            </Space>
                        }
                    >
                        {loading ? (
                            <div style={{ textAlign: "center", padding: 50 }}>
                                <Spin size="large" />
                            </div>
                        ) : (
                            <Table
                                columns={columns}
                                dataSource={products}
                                rowKey="_id"
                                pagination={{ pageSize: 10 }}
                                scroll={{ x: 1200 }}
                            />
                        )}
                    </Card>
                </Col>
            </Row>

            <Modal
                title={`Reorder ${selectedCategory.charAt(0).toUpperCase() + selectedCategory.slice(1)}`}
                open={reorderModalOpen}
                onOk={handleReorderSubmit}
                onCancel={() => setReorderModalOpen(false)}
                confirmLoading={reorderLoading}
                okText="Save Order"
                width={600}
                destroyOnClose
            >
                <div style={{ marginTop: 10 }}>
                    <p style={{ color: "#888", marginBottom: 15 }}>
                        Drag and drop items to change their display order on the website.
                    </p>
                    <DragDropContext
                        onDragEnd={(result) => {
                            if (!result.destination) return;
                            const items = Array.from(reorderList);
                            const [reorderedItem] = items.splice(result.source.index, 1);
                            items.splice(result.destination.index, 0, reorderedItem);
                            setReorderList(items);
                        }}
                    >
                        <Droppable droppableId="reorder-list">
                            {(provided) => (
                                <div {...provided.droppableProps} ref={provided.innerRef}>
                                    {reorderList.map((item, index) => (
                                        <Draggable key={item._id} draggableId={item._id} index={index}>
                                            {(provided, snapshot) => (
                                                <div
                                                    ref={provided.innerRef}
                                                    {...provided.draggableProps}
                                                    style={{
                                                        ...provided.draggableProps.style,
                                                        background: snapshot.isDragging ? "#fafafa" : "#fff",
                                                        padding: "10px",
                                                        border: "1px solid #f0f0f0",
                                                        borderRadius: "4px",
                                                        marginBottom: "8px",
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "space-between",
                                                        boxShadow: snapshot.isDragging ? "0 2px 8px rgba(0,0,0,0.15)" : "none"
                                                    }}
                                                >
                                                    <div style={{ display: "flex", alignItems: "center" }}>
                                                        <Image
                                                            width={40} height={40}
                                                            src={image_url + item.heroImage}
                                                            style={{ objectFit: "cover", borderRadius: 4, marginRight: 15 }}
                                                            preview={false}
                                                            fallback="/placeholder.jpg"
                                                        />
                                                        <span>{item.name}</span>
                                                    </div>
                                                    <DragHandle {...provided.dragHandleProps} />
                                                </div>
                                            )}
                                        </Draggable>
                                    ))}
                                    {provided.placeholder}
                                </div>
                            )}
                        </Droppable>
                    </DragDropContext>
                </div>
            </Modal>
        </div>
    );
}

export default ProductList;